import React, { useEffect, useState } from "react";
import { Button, message, DatePicker, Select, Statistic, Card } from "antd";
import { EditOutlined, PlusOutlined, DownloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import dayjs from 'dayjs';
import ExpansesDrawer from "./ExpansesDrawer";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import { getUserRole } from "../../utils/auth";
import { handleApiError, handleExportError, showSuccessMessage, showWarningMessage } from "../../utils/errorHandler";
import { trackPageView, trackExport, trackUserAction } from "../../utils/monitoring";
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Expanses = () => {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [expanses, setExpanses] = useState([]);
    const [selectedExpanse, setSelectedExpanse] = useState(null);
    const [expanseToDelete, setExpanseToDelete] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [paginationInfo, setPaginationInfo] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    const [userRole, setUserRole] = useState("");
    const [filters, setFilters] = useState({
        store: "",
        startDate: "",
        endDate: "",
    });
    const [isDateFilterSelected, setIsDateFilterSelected] = useState(false);
    const [stores, setStores] = useState([]); // State for stores
    const [selectedStore, setSelectedStores] = useState(''); // State for stores
    const [summary, setSummary] = useState({ totalExpansees: 0, totalAmount: 0 }); // New state for summary
    const [pdfLoading, setPdfLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);

    // const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

    // Function to set default date range (current month from 1st to today)
    const setDefaultDateRange = () => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1; // getMonth() returns 0-11, so add 1
        const firstDay = new Date(currentYear, currentMonth - 1, 1); // Month is 0-indexed
        
        // Format dates without timezone issues
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        const startDate = formatDate(firstDay);
        const endDate = formatDate(today);
        
        console.log('Setting default date range:', { 
            startDate, 
            endDate, 
            today: today.toString(),
            firstDay: firstDay.toString(),
            currentYear,
            currentMonth
        });
        
        setFilters(prev => ({
            ...prev,
            startDate: startDate,
            endDate: endDate
        }));
    };

    useEffect(() => {
        setUserRole(getUserRole());
        setDefaultDateRange(); // Set default date range
        fetchStores(); // Fetch stores on mount
        trackPageView('Expenses'); // Track page view
    }, []); // Run only once on mount

    useEffect(() => {
        if (userRole.role !== 'admin' && stores.length > 0) {
            const store = stores.find((store) => userRole.storeId === store._id);
            setSelectedStores(store || null);
            handleFilterChange("store", store?.name || '');
        } else if (userRole.role === 'admin') {
            setSelectedStores(null);
            handleFilterChange("store", '');
        }
    }, [userRole, stores]); // Run when userRole or stores changes

    const fetchStores = async () => {
        try {
            const response = await API.get("/stores");
            setStores(response.data || []);
        } catch (err) {
            handleApiError(err, "Failed to load stores.");
        }
    };

    const getExpanses = async () => {
        const { current, pageSize } = paginationInfo;
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: current.toString(),
                limit: pageSize.toString(),
                store: filters.store,
                startDate: filters.startDate,
                endDate: filters.endDate,
            }).toString();

            console.log('Frontend sending date filters:', {
                startDate: filters.startDate,
                endDate: filters.endDate,
                store: filters.store
            });

            const response = await API.get(`/expanse/paginated?${queryParams}`);
            const { expanses, pagination, summary: responseSummary } = response?.data || {};
            
            // Data is already sorted by date in descending order (newest first) from backend
            setExpanses(expanses || []);
            setPaginationInfo((prev) => ({
                ...prev,
                total: pagination?.total || 0,
            }));
            setSummary(responseSummary || { totalExpansees: 0, totalAmount: 0 }); // Update summary state
        } catch (err) {
            console.error("Error fetching expenses:", err);
            handleApiError(err, "Failed to fetch expenses. Please try again.");
            // Set empty data on error to prevent UI issues
            setExpanses([]);
            setPaginationInfo((prev) => ({ ...prev, total: 0 }));
            setSummary({ totalExpansees: 0, totalAmount: 0 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getExpanses();

    }, [paginationInfo.current, paginationInfo.pageSize, filters]);

    // Monitor date filters to enable/disable download button
    useEffect(() => {
        const hasDateFilters = filters.startDate && filters.endDate;
        setIsDateFilterSelected(hasDateFilters);
    }, [filters.startDate, filters.endDate]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
        }));
        setPaginationInfo((prev) => ({
            ...prev,
            current: 1, // Reset to first page when filters change
        }));
    };

    const handleDeleteClick = (expanse) => {
        setExpanseToDelete(expanse);
        setIsDeleteModalVisible(true);
    };

    const handleDeleteExpanse = async () => {
        try {
            await API.delete(`/expanse/${expanseToDelete?._id}`);
            showSuccessMessage("Expense deleted successfully!");
            trackUserAction('delete_expense', { expenseId: expanseToDelete?._id });
            getExpanses();
        } catch (err) {
            handleApiError(err, "Failed to delete expense.");
        } finally {
            setIsDeleteModalVisible(false);
        }
    };

    const handleAddExpanse = async (newExpanse) => {
        try {
            await API.post("/expanse", newExpanse);
            showSuccessMessage("Expense added successfully!");
            trackUserAction('add_expense', { expenseHead: newExpanse.expanseHead, amount: newExpanse.amount });
            getExpanses();
        } catch (err) {
            handleApiError(err, "Failed to add expense.");
        }
    };

    const handleEditExpanse = async (updatedExpanse) => {
        try {
            await API.put(`/expanse/${updatedExpanse?._id}`, updatedExpanse);
            showSuccessMessage("Expense updated successfully!");
            trackUserAction('edit_expense', { expenseId: updatedExpanse?._id, expenseHead: updatedExpanse.expanseHead, amount: updatedExpanse.amount });
            getExpanses();
            setOpenDrawer(false);
        } catch (err) {
            handleApiError(err, "Failed to update expense.");
        }
    };

    const columns = [
        {
            title: "Index",
            key: "index",
            render: (_, __, index) =>
                ((paginationInfo.current || 1) - 1) * (paginationInfo.pageSize || 20) + index + 1,
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            render: (date) => {
                const dateObj = new Date(date);
                const day = dateObj.getDate().toString().padStart(2, '0');
                const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
                const year = dateObj.getFullYear();
                return `${day}/${month}/${year}`;
            },
        },
        {
            title: "Expense Head",
            dataIndex: ["expanseHead", "name"],
            key: "expanseHead",
            render: (name) => name || "N/A",
        },
        {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            render: (amount) => `₹${amount.toFixed(2)}`,
        },
        {
            title: "Payment Method",
            dataIndex: "paymentMethod",
            key: "paymentMethod",
            render: (method) => method || "Not Specified",
        },
        {
            title: "Bill",
            dataIndex: "bill",
            key: "bill",
            render: (bill) => {
                if (!bill) return "N/A";
                const fileName = bill.split("/").pop() || "Bill.pdf";
                return (
                    <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        style={{ padding: 0, height: 'auto' }}
                    >
                        {fileName}
                    </Button>
                );
            },
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            render: (desc) => desc || "N/A",
        },
        {
            title: "Store",
            dataIndex: "store",
            key: "store",
            render: (store) => store || "N/A",
        },
        {
            title: "Created By",
            dataIndex: "billCreatedBy",
            key: "billCreatedBy",
            render: (createdBy) => createdBy.name || "N/A",
        },
        userRole.role === 'admin' && {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (record) => (
                <div>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                            setIsEdit(true);
                            setSelectedExpanse(record);
                            setOpenDrawer(true);
                        }}
                    />
                    {/* <Button
                        danger
                        icon={<DeleteOutlined />}
                        style={{ marginLeft: 8 }}
                        onClick={() => handleDeleteClick(record)}
                    /> */}
                </div>
            ),
        },
    ].filter(Boolean);

    const handlePaginationChange = (paginationConfig) => {
        console.log("Pagination change:", paginationConfig);
        const { current, pageSize } = paginationConfig;
        setPaginationInfo((prev) => ({
            ...prev,
            current: current || 1,
            pageSize: pageSize || 20,
        }));
    };

    // Excel Export Function - Downloads all records for selected date range
    const handleDownloadExcel = async () => {
        if (!isDateFilterSelected) {
            showWarningMessage("Please select a date range to download data");
            return;
        }

        setExcelLoading(true);
        try {
            console.log("Starting Excel generation for Expenses with date range:", filters.startDate, "to", filters.endDate);
            
            // Fetch all records for the selected date range (not just current page)
            const response = await API.get("/expanse/paginated", {
                params: {
                    page: 1,
                    limit: 10000, // Large limit to get all records
                    store: filters.store,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                },
            });

            const allExpenses = response?.data?.expanses || [];
            
            if (allExpenses.length === 0) {
                showWarningMessage("No data available for the selected date range");
                return;
            }

            console.log(`Fetched ${allExpenses.length} records for Excel export`);
            
            // Dynamically import xlsx with robust error handling
            let XLSX;
            try {
                const xlsxModule = await import('xlsx');
                console.log("xlsxModule:", xlsxModule);
                XLSX = xlsxModule.default || xlsxModule;
                console.log("XLSX object:", XLSX);
                console.log("XLSX.utils:", XLSX?.utils);
                
                if (!XLSX || !XLSX.utils) {
                    throw new Error("XLSX library not properly loaded");
                }
                
                console.log("XLSX imported successfully for Expenses");
            } catch (importError) {
                console.error("Failed to import XLSX for Expenses:", importError);
                handleExportError(importError, 'Excel');
                return;
            }
            
            // Prepare data for Excel with error handling - using all records with sequential indexing
            const excelData = allExpenses.map((item, index) => ({
                'Index': index + 1, // Sequential numbering for all records
                'Date': item.date ? (() => {
                    const dateObj = new Date(item.date);
                    const day = dateObj.getDate().toString().padStart(2, '0');
                    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
                    const year = dateObj.getFullYear();
                    return `${day}/${month}/${year}`;
                })() : "N/A",
                'Expense Head': item.expanseHead?.name || "N/A",
                'Amount': item.amount ? parseFloat(item.amount).toFixed(2) : "0.00",
                'Paymode': item.paymentMethod || "Not Specified",
                'Bill': item.bill ? (item.bill.split("/").pop() || "Bill.pdf") : "N/A",
                'Description': item.description || "N/A",
                'Store': item.store || "N/A",
                'Created By': item.billCreatedBy?.name || "N/A"
            }));

            const storeNameHeader = filters.store && filters.store !== '' ? filters.store : 'All Stores';
            // Create a worksheet with the store name at the top
            const headerRow = [{ 'A': `Store: ${storeNameHeader}` }];
            const worksheet = XLSX.utils.json_to_sheet(headerRow, { skipHeader: true });
            // Add main data below, starting at row 3
            XLSX.utils.sheet_add_json(worksheet, excelData, { origin: 'A3' });
            // Set column widths
            const columnWidths = [
                { wch: 10 }, // Index
                { wch: 15 }, // Date
                { wch: 35 }, // Expense Head - increased width
                { wch: 15 }, // Amount
                { wch: 15 }, // Paymode - shorter text
                { wch: 20 }, // Bill
                { wch: 30 }, // Description
                { wch: 20 }, // Store
                { wch: 20 }  // Created By
            ];
            worksheet['!cols'] = columnWidths;

            // Add summary data to a separate sheet
            const totalAmount = parseFloat(summary.totalAmount || 0);
            const avgAmount = summary.totalExpansees > 0 ? totalAmount / summary.totalExpansees : 0;
            
            // Payment method breakdown
            const paymentMethods = {};
            expanses.forEach(expense => {
                const method = expense.paymentMethod || "Not Specified";
                paymentMethods[method] = (paymentMethods[method] || 0) + 1;
            });

            const summaryData = [
                { 'Metric': 'Total Number of Expenses', 'Value': summary.totalExpansees || 0 },
                { 'Metric': 'Total Amount', 'Value': `Rs. ${totalAmount.toFixed(2)}` },
                { 'Metric': 'Average Amount per Expense', 'Value': `Rs. ${avgAmount.toFixed(2)}` },
                { 'Metric': '', 'Value': '' }, // Empty row for spacing
                { 'Metric': 'Payment Method Breakdown', 'Value': '' },
                ...Object.entries(paymentMethods).map(([method, count]) => ({
                    'Metric': method,
                    'Value': `${count} expenses`
                }))
            ];

            const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
            summaryWorksheet['!cols'] = [{ wch: 30 }, { wch: 20 }];

            // Create workbook and worksheets
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Expenses Report');
            XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

            // Generate filename
            const timestamp = new Date().toISOString().split('T')[0];
            const dateRange = `${filters.startDate}_to_${filters.endDate}`;
            const filename = `Expenses_Report_${dateRange}_${timestamp}.xlsx`;

            console.log("Saving Excel file with filename:", filename);
            
            // Download Excel file
            XLSX.writeFile(workbook, filename);
            showSuccessMessage("Excel file downloaded successfully!");
            trackExport('Excel', true);
            trackUserAction('export_expenses_excel', { 
                recordCount: expanses.length, 
                totalAmount: summary.totalAmount 
            });
        } catch (error) {
            console.error("Error generating Excel for Expenses:", error);
            handleExportError(error, 'Excel');
            trackExport('Excel', false);
        } finally {
            setExcelLoading(false);
        }
    };

    return (
        <div
            style={{
                padding: "20px",
                background: "#fff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            }}
        >
            <div style={{ marginBottom: 16, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                    <Select
                        placeholder="Filter by Store"
                        value={selectedStore?.name || filters.store || undefined}
                        onChange={(value) => handleFilterChange("store", value || '')}
                        style={{ width: 200 }}
                        allowClear
                        disabled={userRole.role !== 'admin'}
                    >
                        {stores.map((store) => (
                            <Option key={store._id} value={store.name}>
                                {store.name}
                            </Option>
                        ))}
                    </Select>
                    <RangePicker
                        value={filters.startDate && filters.endDate ? [dayjs(filters.startDate), dayjs(filters.endDate)] : []}
                        onChange={(dates) => {
                            if (dates && dates.length === 2) {
                                // Update both dates at once to prevent race conditions
                                const newFilters = {
                                    ...filters,
                                    startDate: dates[0].format("YYYY-MM-DD"),
                                    endDate: dates[1].format("YYYY-MM-DD")
                                };
                                console.log("Date range selected:", dates[0].format("YYYY-MM-DD"), "to", dates[1].format("YYYY-MM-DD"));
                                setFilters(newFilters);
                                setPaginationInfo(prev => ({ ...prev, current: 1 }));
                                getExpanses();
                            } else {
                                // Clear both dates at once
                                const newFilters = {
                                    ...filters,
                                    startDate: "",
                                    endDate: ""
                                };
                                console.log("Date range cleared");
                                setFilters(newFilters);
                                setPaginationInfo(prev => ({ ...prev, current: 1 }));
                                setIsDateFilterSelected(false);
                                getExpanses();
                            }
                        }}
                        format="DD/MM/YYYY"
                        style={{ width: 300 }}
                        placeholder={['Start Date', 'End Date']}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setIsEdit(false);
                            setSelectedExpanse(null);
                            setOpenDrawer(true);
                        }}
                    >
                        Add New Expense
                    </Button>
                    <Button
                        type="primary"
                        icon={<FileExcelOutlined />}
                        loading={excelLoading}
                        disabled={!isDateFilterSelected}
                        onClick={handleDownloadExcel}
                        style={{ 
                            background: isDateFilterSelected 
                                ? 'linear-gradient(135deg, #00c851 0%, #007e33 100%)' 
                                : '#d9d9d9', 
                            border: 'none', 
                            color: isDateFilterSelected ? '#fff' : '#999', 
                            fontWeight: 600, 
                            minWidth: 140 
                        }}
                        title={!isDateFilterSelected ? "Please select date range to enable download" : "Download all records for selected date range"}
                    >
                        {excelLoading ? "Generating Excel..." : "Download Excel"}
                    </Button>
                </div>
            </div>

            {/* Summary Header */}
            <Card
                bordered={false}
                style={{
                    marginBottom: 16,
                    background: 'linear-gradient(135deg,rgb(100, 82, 255),rgb(20, 56, 255))', // Warm red-orange gradient for emphasis
                    color: '#fff',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    padding: '1px',
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Statistic
                        title="Number of Expenses"
                        value={summary.totalExpansees || 0}
                        style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}
                        valueStyle={{ color: '#fff' }}
                    />
                    <Statistic
                        title="Total Expenses"
                        value={parseFloat(summary.totalAmount || 0).toFixed(2)}
                        prefix={<span style={{ fontSize: '18px', color: '#fff' }}>₹</span>}
                        style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}
                        valueStyle={{ color: '#fff' }}
                    />
                </div>
            </Card>

            <CustomTable
                columns={columns}
                dataSource={expanses}
                onPaginationChange={handlePaginationChange}
                loading={loading}
                style={{ padding: "0 5px" }}
                total={paginationInfo.total}
                current={paginationInfo.current}
                pageSize={paginationInfo.pageSize}
            />

            <ExpansesDrawer
                open={openDrawer}
                setOpen={setOpenDrawer}
                data={selectedExpanse}
                isEdit={isEdit}
                onSubmit={isEdit ? handleEditExpanse : handleAddExpanse}
                stores={stores} // Pass stores to drawer
            />

            <ConfirmationModal
                visible={isDeleteModalVisible}
                onConfirm={handleDeleteExpanse}
                onCancel={() => setIsDeleteModalVisible(false)}
                title="Confirm Deletion"
                content="Are you sure you want to delete this expense? This action cannot be undone."
            />
        </div>
    );
};

export default Expanses;